import buildURL from 'axios/lib/helpers/buildURL'
import useBackend from '@/hooks/useBackend'

import { isRequired } from '@/utils/CustomErrors'

import config from '@/config'

const { setup, doDelete, doGet, doPost, getBaseUrl } = useBackend()
setup('/ovpv3', config.microserviceBaseUrl, {
  responseSuccess: response => {
    return response.data
  },
  responseError: error => {
    return error
  },
})

export default function useOvp() {
  function createOrUpdateAdvisoryProtocol(payload, { jwToken = isRequired('jwToken') }) {
    const { basketId = isRequired('basketId'), submit = isRequired('submit') } = payload // query/url params must match payload

    const path = buildURL(`/baskets/${basketId}/advisory-protocol`, { jwToken, submit })
    return doPost(path, { ...payload }).then(response => response.data)
  }

  function createOvpSignature(
    basketId = isRequired('basketId'),
    personId = isRequired('personId'),
    { jwToken, signature }
  ) {
    const url = buildURL(`/baskets/${basketId}/signature/${personId}`, {
      jwToken,
    })
    return doPost(url, { signature })
  }

  function createBasketForPartnerNumber({
    payload = isRequired('payload'),
    partnerNumber = isRequired('partnerNumber'),
    uid = isRequired('uid'),
  }) {
    const url = buildURL('/userregistry/baskets', { partnerNumber, uid })
    return doPost(url, payload).then(response => response.data)
  }

  function deleteAdvisoryProtocol({ basketId = isRequired('basketId'), jwToken = isRequired('jwToken') }) {
    const path = buildURL(`/baskets/${basketId}/advisory-protocol`, { jwToken })
    return doDelete(path).then(response => response.data)
  }

  function deleteOvpSignature(basketId = isRequired('basketId'), personId = isRequired('personId'), { jwToken }) {
    const url = buildURL(`/baskets/${basketId}/signature/${personId}`, {
      jwToken,
    })
    return doDelete(url)
  }

  function getAdvisoryProtocol({ basketId = isRequired('basketId'), jwToken = isRequired('jwToken') }) {
    return doGet(`/baskets/${basketId}/advisory-protocol`, { jwToken }).then(response => response.data)
  }

  function getAdvisoryProtocolDocuments({ basketId = isRequired('basketId'), jwToken = isRequired('jwToken') }) {
    return doGet(`/baskets/${basketId}/advisory-protocol/documents`, { jwToken }).then(response => response.data)
  }

  function getDistributionPartnerInfo({
    distributionPartnerNumber = isRequired('distributionPartnerNumber'),
    contractStartDate = isRequired('contractStartDate'),
    language = isRequired('language'),
  }) {
    return doGet(`/distributionpartner/${distributionPartnerNumber}`, {
      contractStartDate,
      language,
    }).then(response => response.data)
  }

  function getDistributionPartnerOrganizationName({
    distributionPartnerNumber = isRequired('distributionPartnerNumber'),
    contractStartDate = isRequired('contractStartDate'),
    language = isRequired('language'),
  }) {
    return doGet(`/distributionpartner/${distributionPartnerNumber}/organizationName`, {
      contractStartDate,
      language,
    }).then(response => response.data)
  }

  // @todo don't return a promise here. I don't see where building the url here should fail. Promise can't be used in computed, so it's inconvenient
  function getDocumentUrl(
    { basketId = isRequired('basketId'), jwToken = isRequired('jwToken') },
    type = isRequired('type')
  ) {
    const path = buildURL(`${getBaseUrl.value}/baskets/${basketId}/document.pdf`, {
      jwToken,
      type,
    })
    return Promise.resolve(path)
  }

  function getOverviewPdfUrl({ basketId = isRequired('basketId'), jwToken = isRequired('jwToken') }) {
    return buildURL(`${getBaseUrl.value}/baskets/${basketId}/overview.pdf`, {
      jwToken,
    })
  }

  function getRiskAssessment(basket) {
    return doPost('/risk-assessment', basket)
  }

  function fetchOvpSignature({
    basketId = isRequired('basketId'),
    jwToken = isRequired('jwToken'),
    personId = isRequired('personId'),
  }) {
    return doGet(`/baskets/${basketId}/signature/${personId}`, { jwToken })
  }

  function sendSMSTan(token = isRequired('token')) {
    return doGet('/baskets/smstan', { token }).then(response => response.data)
  }

  function signOvpDocument(basketId = isRequired('basketId'), { jwToken = isRequired('jwToken') }) {
    return doGet(`/baskets/${basketId}/signDocument`, { jwToken })
  }

  function signupOvpContest({ basketId = isRequired('basketId'), jwToken = isRequired('jwToken') }) {
    const path = buildURL(`/baskets/${basketId}/contest`, { jwToken })
    return doPost(path).then(response => response.data)
  }

  return {
    createBasketForPartnerNumber,
    createOrUpdateAdvisoryProtocol,
    createOvpSignature,
    deleteAdvisoryProtocol,
    deleteOvpSignature,
    getAdvisoryProtocol,
    getAdvisoryProtocolDocuments,
    getDistributionPartnerInfo,
    getDistributionPartnerOrganizationName,
    getDocumentUrl,
    getOverviewPdfUrl,
    // getProducts,
    getRiskAssessment,
    fetchOvpSignature,
    sendSMSTan,
    signOvpDocument,
    signupOvpContest,
  }
}
